import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import {
  selectBookingAppointmentData, selectBookingData, selectBookingSpecialist, selectBookingTimezone,
} from '../../redux/slices/booking';
import { formatAppointmentWhen } from '../../utils/booking-flow';
import { generateSpecialities, getSpecialistDisplayName } from '../../utils/specialist';
import FeeCard from '../FeeCard';

const NOT_CHOSEN = 'Not chosen yet';

export const useAppointmentSummary = () => {
  const specialist = useSelector(selectBookingSpecialist);
  const appointment = useSelector(selectBookingAppointmentData);
  const timezone = useSelector(selectBookingTimezone);
  const { speciality } = useSelector(selectBookingData);
  // The specialty being booked, not every one a multi-specialty clinician practises; the
  // clinician's own list is the fallback (e.g. before the specialty is known on a direct link).
  const booked = speciality?.name && specialist?.specialities?.includes(speciality.name) ? speciality.name : undefined;
  return {
    timezone,
    clinician: specialist ? getSpecialistDisplayName(specialist) : null,
    specialty: specialist ? booked || generateSpecialities(specialist.specialities) || null : null,
    when: formatAppointmentWhen(appointment),
  };
};

const Line: FC<{ label: string, value: string | null }> = ({ label, value }) => (
  <div className="line">
    <span className="k">{label}</span>
    <span className={value ? 'v' : 'v ph'}>{value ?? NOT_CHOSEN}</span>
  </div>
);

// The side panel of each step: the appointment so far, the fee estimate and what the patient agrees to.
const BookingSummary: FC = () => {
  const {
    clinician, specialty, when, timezone,
  } = useAppointmentSummary();

  return (
    <>
      <section className="mini" aria-label="Your appointment">
        <h4>Your appointment</h4>
        <Line label="Clinician" value={clinician} />
        <Line label="Specialty" value={specialty} />
        <Line label="When" value={when} />
        <Line label="Timezone" value={timezone} />
      </section>
      <FeeCard />
      <section className="mini" aria-label="What you're agreeing to">
        <h4>What you&apos;re agreeing to</h4>
        <p>
          Your payment link arrives by SMS after you book. Pay it to secure your appointment — if we haven&apos;t received payment 3 business days before, we&apos;ll send you a reminder. Missed appointments, or appointments cancelled without valid medical reason within 2 business days of the appointment will incur a fee ($150).
        </p>
      </section>
    </>
  );
};

export default BookingSummary;
